import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import iconExclusiveContent from "../../../Main/Landing/images/Vault/exclusive-content.png";
import iconDirectCreator from "../../../Main/Landing/images/Vault/direct-creator.png";
import iconPremiumCreator from "../../../Main/Landing/images/Vault/premium-creator-pink.png";
import iconVerifiedMember from "../../../Main/Landing/images/Vault/verified-member.png";
import iconPrivateCommunity from "../../../Main/Landing/images/Vault/private-community.png";

const useStyles = makeStyles((theme) => ({
    membershipSection: {
        padding: '60px 5%',
        backgroundColor: '#0a0a0a',
        [theme.breakpoints.down('sm')]: {
            padding: '45px 5%',
        }
    },
    membershipContainer: {
        maxWidth: 1314,
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    membershipHeader: {
        marginBottom: '40px',
        textAlign: 'left',
    },
    membershipTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: '52px',
        fontWeight: 400,
        lineHeight: '63px',
        color: '#ffffff',
        marginBottom: '20px',
        textAlign: "center",
        [theme.breakpoints.down('sm')]: {
            fontSize: '36px',
            lineHeight: '24px',
        }
    },
    membershipSubtitle: {
        textAlign: "center",
        fontSize: '18px',
        fontWeight: 200,
        lineHeight: '27px',
        color: '#ffffff',
        fontFamily: "'Roboto', sans-serif",
        [theme.breakpoints.down('sm')]: {
            fontSize: '16px',
            lineHeight: '24px',
        }
    },
    benefitsGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        gap: '16px',
        marginBottom: '40px',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            marginBottom: '32px',
        }
    },
    benefitCard: {
        backgroundColor: '#1E1E1E',
        borderRadius: '12px',
        padding: '20px 16px',
        textAlign: 'left',
        flex: '1 1 0',
        minWidth: '220px',
        boxSizing: 'border-box',
        border: '1px solid transparent',
        boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.35)',
            boxShadow: '6px 6px 24px rgba(255, 255, 255, 0.12), 0 0 12px rgba(255, 255, 255, 0.04)',
        },
    },
    benefitIcon: {
        width: 40,
        height: 40,
        marginBottom: '32px',
        display: 'block',
        objectFit: 'contain',
        [theme.breakpoints.down('sm')]: {
            marginBottom: '26px',
            width: 38.4,
            height: 38.4,
        }
    },
    benefitTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: '24px',
        fontWeight: 500,
        lineHeight: '100%',
        color: '#ffffff',
        marginBottom: '12px',
        whiteSpace: 'pre-wrap',
        [theme.breakpoints.down('sm')]: {
            marginTop: '20px',
            whiteSpace: 'normal',
            fontSize: '20px'
        }
    },
    benefitDesc: {
        fontSize: '18px',
        fontWeight: 200,
        color: '#ffffff',
        lineHeight: '27px',
        [theme.breakpoints.down('sm')]: {
            marginTop: '10px',
            whiteSpace: 'normal',
            lineHeight: '24px',
            fontSize: '16px',
            fontFamily: "Roboto, Arial, sans-serif",
        }
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '40px',
        [theme.breakpoints.down('sm')]: {
            marginTop: '16px',
        }
    },
    becomeMemberBtn: {
        background: 'linear-gradient(90deg, #87959C 1.44%, #89959C 7.69%, #8B949B 13.94%, #B1B3BB 21.15%, #C3C8CC 31.25%, #F1F2F4 38.94%, #D2DBE6 51.44%, #D2DBE6 63.46%, #9AB0B7 81.73%, #A5B7C0 94.71%)',
        color: '#111111',
        padding: '0px 32px',
        height: "48px",
        borderRadius: '6px',
        fontSize: '16px',
        fontWeight: 600,
        border: 'none',
        cursor: 'pointer',
        textTransform: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
            opacity: 0.9,
            transform: 'translateY(-1px)',
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        }
    }
}));

const MEMBERSHIP_BENEFITS = [
    { title: "Exclusive\ncontent", desc: "Material made only for Vault members.", icon: iconExclusiveContent },
    { title: "Direct creator\ninteraction", desc: "Message and request, creator to member.", icon: iconDirectCreator },
    { title: "Premium creator\nrequests", desc: "Ask for custom work, priced by the creator.", icon: iconPremiumCreator },
    { title: "Verified member\naccess", desc: "Every profile you meet here is checked and real.", icon: iconVerifiedMember },
    { title: "Private\ncommunity", desc: "A quieter corner of the platform, members only.", icon: iconPrivateCommunity }
];

export const VaultMembershipSection: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();

    return (
        <Box className={classes.membershipSection}>
            <Box className={classes.membershipContainer}>
                <Box className={classes.membershipHeader}>
                    <Typography variant="h2" className={classes.membershipTitle}>
                        Why Become a Member?
                    </Typography>
                    <Typography className={classes.membershipSubtitle}>
                        Membership pays for itself in access alone.
                    </Typography>
                </Box>

                <Box className={classes.benefitsGrid}>
                    {MEMBERSHIP_BENEFITS.map((benefit, index) => (
                        <Box key={index} className={classes.benefitCard}>
                            <img src={benefit.icon} alt={benefit.title.replace('\n', ' ')} className={classes.benefitIcon} />
                            <Typography className={classes.benefitTitle}>
                                {benefit.title}
                            </Typography>
                            <Typography className={classes.benefitDesc}>
                                {benefit.desc}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box className={classes.buttonContainer}>
                    <button className={classes.becomeMemberBtn} onClick={() => history.push('/member-onboarding-step')}>
                        Become A Member
                    </button>
                </Box>
            </Box>
        </Box>
    );
};
